import { JSX, useState } from 'react';
import { useGetManyReference, usePermissions } from 'react-admin';
import {
    Alert,
    Box,
    Button,
    Chip,
    LinearProgress,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import { AccountAllowances } from '../CustomerAccount/hooks/customer-account-allowances';
import { useAddCompanyDomain } from '../CustomerAccount/hooks/customer-account-domains';
import {
    addDomainBlockedReason,
    domainStatusInk,
    domainStatusLabel,
    domainTokenNote,
    looksLikeADomain
} from '../CustomerAccount/domain-panel-format';
import { permissionManager } from "../../../permissions/manager";

type CompanyDomainsPanelProps = {
    companyId: string|number|undefined,
    /** The account whose domain pool this spends, or undefined for a CRM company. */
    customerAccountId: string|number|undefined,
    allowances: AccountAllowances|undefined,
    loadingAllowances: boolean,
    /** Called after a domain is added, so the allowance figures above can be read again. */
    onDomainAdded?: () => void,
};

/**
 * A company's domains, and the one control that adds another.
 *
 * HQ had no way to add a domain until 2026-09-10. An account that needed one had to be talked
 * through the portal, or a `Website` row had to be written from the sidebar's Websites screen,
 * which asks for a status and a verification token as free text and produces a row the portal
 * cannot verify.
 *
 * **Verification stays with the customer, and so does the token.** The TXT value is a credential
 * the customer publishes in their own DNS, and they read it from their Organisations screen in
 * the portal, which is also where the button that issues a new one lives. So this panel shows how
 * far a domain has got and what to tell the customer, and never the value itself. That is the
 * user's decision, taken when this was specified.
 */
export const CompanyDomainsPanel: (props: CompanyDomainsPanelProps) => JSX.Element =
    (props: CompanyDomainsPanelProps): JSX.Element => {
        const { permissions } = usePermissions();
        const [domain, setDomain] = useState<string>('');
        const { addingDomain, domainError, domainSuccess, addDomain, clearDomainMessages } = useAddCompanyDomain();
        const { data, isPending, error, refetch } = useGetManyReference(
            'websites',
            {
                target: 'company',
                id: props.companyId as string|number,
                pagination: { page: 1, perPage: 50 },
                sort: { field: 'id', order: 'ASC' },
                filter: {},
            },
            { enabled: props.companyId !== undefined && props.companyId !== '' }
        );

        const mayAdd: boolean = permissionManager.isAllowed(permissions, 'HQ_Data::companies.create');
        const blockedReason: string|undefined = addDomainBlockedReason({
            loading: props.loadingAllowances,
            pool: props.allowances?.domains,
            hasCustomerAccount: props.customerAccountId !== undefined && props.customerAccountId !== '',
        });
        // The typed value is judged twice on purpose: here, to stop the obvious mistakes without
        // a round trip, and by the API, whose refusal is the one that is shown.
        const typedIsADomain: boolean = looksLikeADomain(domain);

        async function submitDomain(): Promise<void> {
            if (props.companyId === undefined || !typedIsADomain) {
                return;
            }
            const added = await addDomain(props.companyId, domain);
            if (added !== undefined) {
                setDomain('');
                await refetch();
                props.onDomainAdded?.();
            }
        }

        return (
            <Box sx={ { width: '100%', padding: '4px 0 12px 0' } }>
                <Typography sx={ {
                    fontSize: 'var(--wf-eyebrow, 11px)',
                    letterSpacing: 'var(--wf-track, 0.08em)',
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                    fontWeight: 700,
                    marginBottom: '6px',
                } }>
                    { 'Domains' }
                </Typography>

                { (isPending) &&
                    <LinearProgress sx={ { margin: '10px 0', width: '50%' } } />
                }
                { (error !== null && error !== undefined) &&
                    <Alert severity="warning" sx={ { marginBottom: '10px' } }>
                        { 'Could not read this company\'s domains.' }
                    </Alert>
                }

                { (!isPending && (data ?? []).length === 0) &&
                    <Typography sx={ { fontSize: '13px', color: 'text.secondary', marginBottom: '8px' } }>
                        { 'No domains yet. A company needs one before its tools can run.' }
                    </Typography>
                }

                { (!isPending && (data ?? []).length > 0) &&
                    <Table size="small" sx={ { marginBottom: '10px' } }>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={ { fontWeight: 700 } }>{ 'Domain' }</TableCell>
                                <TableCell sx={ { fontWeight: 700 } }>{ 'Status' }</TableCell>
                                <TableCell sx={ { fontWeight: 700 } }>{ 'What the customer has to do' }</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            { (data ?? []).map( (website: any): JSX.Element => {
                                const note: string = domainTokenNote(
                                    website?.status,
                                    website?.verificationTokenExpiration === undefined || website?.verificationTokenExpiration === null
                                        ? null
                                        : Math.floor(new Date(website.verificationTokenExpiration).getTime() / 1000)
                                );

                                return (
                                    <TableRow key={ website?.id }>
                                        <TableCell sx={ { fontFamily: 'var(--wf-font-mono, monospace)' } }>
                                            { website?.baseUrl }
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={ domainStatusLabel(website?.status) }
                                                  size="small"
                                                  variant="outlined"
                                                  sx={ {
                                                      color: domainStatusInk(website?.status),
                                                      borderColor: domainStatusInk(website?.status),
                                                      fontWeight: 600,
                                                  } } />
                                        </TableCell>
                                        <TableCell sx={ { fontSize: '13px', color: 'text.secondary' } }>
                                            { note === '' ? 'Nothing. This domain is verified.' : note }
                                        </TableCell>
                                    </TableRow>
                                );
                            } ) }
                        </TableBody>
                    </Table>
                }

                { (mayAdd) &&
                    <Stack direction="row" spacing={ 1 } sx={ { alignItems: 'flex-start', flexWrap: 'wrap' } }>
                        <TextField
                            label="Add a domain"
                            placeholder="https://example.com"
                            size="small"
                            value={ domain }
                            disabled={ blockedReason !== undefined || addingDomain }
                            onChange={ (event: any): void => {
                                clearDomainMessages();
                                setDomain(event.target.value);
                            } }
                            onKeyDown={ (event: any): void => {
                                if (event.key === 'Enter') {
                                    event.preventDefault();
                                    submitDomain();
                                }
                            } }
                            error={ domain.trim() !== '' && !typedIsADomain }
                            helperText={
                                blockedReason !== undefined
                                    ? blockedReason
                                    : domain.trim() !== '' && !typedIsADomain
                                        ? 'Enter a domain on its own, such as https://example.com'
                                        : 'The customer publishes the TXT record from their portal to verify it.'
                            }
                            sx={ { minWidth: '280px', maxWidth: '380px', flex: '0 1 380px' } }
                        />
                        <Button variant="outlined"
                                sx={ { marginTop: '4px' } }
                                disabled={ blockedReason !== undefined || addingDomain || !typedIsADomain }
                                onClick={ (): void => { submitDomain(); } }>
                            { addingDomain ? 'Adding' : 'Add domain' }
                        </Button>
                    </Stack>
                }

                { (domainError !== '') &&
                    <Alert severity="error" sx={ { marginTop: '10px' } }>{ domainError }</Alert>
                }
                { (domainSuccess !== '') &&
                    <Alert severity="success" sx={ { marginTop: '10px' } }>
                        { domainSuccess + ' It stays unverified until they publish the TXT record, which they get from their Organisations screen.' }
                    </Alert>
                }
            </Box>
        );
    }
